import {
  CloseCircleOutlined,
  LoadingOutlined,
  RightOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import { useEffect, useState } from "react";

import type { CopilotToolCall } from "./types";
import styles from "./styles.module.scss";

const SPECIAL_TOOL_NAMES: Record<string, string> = {
  now: "Check Current Time",
  get_current_time: "Check Current Time",
};

const readableName = (name: string): string =>
  SPECIAL_TOOL_NAMES[name] ??
  name
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const ToolStatusIcon = ({ tool }: { tool: CopilotToolCall }) => {
  if (tool.status === "running" || tool.status === "pending") {
    return <LoadingOutlined spin />;
  }
  if (tool.status === "failed" || tool.isError) {
    return <CloseCircleOutlined />;
  }
  return null;
};

// Raw tool inputs/outputs can carry clinical data and internal API details, so
// (like the legacy Neo page) they're only expandable in developer mode,
// ``?showtools=true`` in the search or hash query. Everyone else gets the
// name/status pill.
const readShowToolsFlag = (): boolean => {
  if (typeof window === "undefined") return false;
  const re = /[?&]showtools=true(?:&|$|#)/i;
  return re.test(window.location.search) || re.test(window.location.hash);
};

const useShowToolsFlag = (): boolean => {
  const [enabled, setEnabled] = useState(readShowToolsFlag);
  useEffect(() => {
    const sync = () => setEnabled(readShowToolsFlag());
    sync();
    window.addEventListener("popstate", sync);
    window.addEventListener("hashchange", sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("hashchange", sync);
    };
  }, []);
  return enabled;
};

const ToolCallSummary = ({
  tool,
  expandable,
}: {
  tool: CopilotToolCall;
  expandable: boolean;
}) => (
  <>
    <ToolOutlined />
    <span className={styles.toolName}>{readableName(tool.name)}</span>
    <span className={styles.toolStatus}>
      <ToolStatusIcon tool={tool} />
      <span
        className={classNames({
          [styles.toolStatusRunning]: tool.status === "running",
        })}
      >
        {tool.status === "pending"
          ? "Queued"
          : tool.status === "running"
          ? "Running"
          : tool.status === "completed"
          ? "Completed"
          : readableName(tool.status)}
      </span>
    </span>
    {expandable && <RightOutlined className={styles.toolChevron} />}
  </>
);

const ToolCallList = ({ tools }: { tools: CopilotToolCall[] }) => {
  const showTools = useShowToolsFlag();
  return (
    <div className={styles.tools} aria-label="Tools used">
      {tools.map((tool) =>
        showTools ? (
          <details className={styles.tool} key={tool.id}>
            <summary>
              <ToolCallSummary tool={tool} expandable />
            </summary>
            {(tool.input !== undefined || tool.output !== undefined) && (
              <div className={styles.toolDetails}>
                {tool.input !== undefined && (
                  <>
                    <span>Input</span>
                    <pre>{JSON.stringify(tool.input, null, 2)}</pre>
                  </>
                )}
                {tool.output !== undefined && (
                  <>
                    <span>{tool.isError ? "Error" : "Output"}</span>
                    <pre>{JSON.stringify(tool.output, null, 2)}</pre>
                  </>
                )}
              </div>
            )}
          </details>
        ) : (
          <div className={styles.tool} key={tool.id}>
            <div className={styles.toolSummary}>
              <ToolCallSummary tool={tool} expandable={false} />
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ToolCallList;
